/**
 * The dealer workspace's scheduler slice of the data provider (DataProvider.scheduler): Store setup's scheduler card and
 * how each appointment from a call was booked (work_packages/crm/01_SCHEDULER_PLAN.md §6.8-6.9). Switch `scheduler` on
 * the store; the API answers 404 feature_disabled while it is off. Live only: the fixture tenant is a workshop, so its
 * slice answers "not switched on" the same way.
 *   GET/PUT /v1/app/scheduler/connection   POST /v1/app/scheduler/connection/test
 *   GET /v1/app/scheduler/appointments     POST /v1/app/scheduler/appointments/:id/retry (Idempotency-Key)
 */
import { APP, ApiError, type Http } from '@app/core/http';

export interface SchedulerRules {
  min_notice_min: number;
  max_days: number;
  offer_count: number;
  open_hours_only: boolean;
  transport_ai_may_book: string[];
  new_customers: string;
  notes_prefix: string;
}
export interface SchedulerConnection {
  id: string | null;
  vendor: string;
  vendor_label: string;
  in_use: string | null;
  in_use_label: string | null;
  mode: 'off' | 'shadow' | 'live';
  state: string;
  state_label: string;
  degraded_reason: string | null;
  last_ok_at: string | null;
  last_error_code: string | null;
  rules: SchedulerRules;
  version: number;
  sample: boolean;
  direct_booking: boolean;
  live: boolean;
  shadow: boolean;
  not_live_reason: string | null;
  certified: boolean;
  activation_steps: string | null;
  status_line: string;
  changes_visible: boolean;
}
export interface ServiceMapRow {
  our_key: string;
  external_code: string;
  external_label: string | null;
  duration_min: number | null;
  ai_may_book: boolean;
}
export interface SchedulerSetup {
  connection: SchedulerConnection;
  service_map: ServiceMapRow[];
  menu: { code: string; name: string; minutes: number | null }[];
  choices: { key: string; label: string; certified: boolean }[];
}
export interface SetupSave {
  version: number;
  in_use?: string | null;
  vendor?: 'assisted' | 'sandbox';
  rules?: Partial<SchedulerRules>;
  service_map?: ServiceMapRow[];
}
export interface TestResult {
  vendor: string;
  vendor_label: string;
  health: { ok: boolean; latency_ms?: number; error?: string };
  availability: { ok: boolean; count?: number; latency_ms?: number; error?: string };
}
export interface SchedulerAppointment {
  id: string;
  call_id: string | null;
  entry_id: string | null;
  status: string;
  status_label: string;
  booked: boolean;
  booked_by: 'assistant' | 'store_staff';
  booked_by_label: string;
  vendor: string | null;
  vendor_label: string | null;
  sample: boolean;
  external_ref: string | null;
  starts_at: string;
  ends_at: string | null;
  label: string;
  transport: string | null;
  advisor_name: string | null;
  failure_code: string | null;
  reason_text: string | null;
  can_retry: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}
/** The appointment list, with the read-only facts the store's advisors need (no settings permission). */
export interface SchedulerAppointments {
  items: SchedulerAppointment[];
  changes_visible: boolean;
  timezone: string;
  connection: { live: boolean; vendor: string; vendor_label: string; sample: boolean } | null;
  service_map: { our_key: string; external_code: string; external_label: string | null; duration_min: number | null }[];
  menu: { code: string; name: string }[];
}
export interface Api {
  setup(): Promise<SchedulerSetup>;
  save(body: SetupSave): Promise<SchedulerSetup>;
  test(): Promise<TestResult>;
  appointments(q: { from?: string; to?: string; status?: string; limit?: number }): Promise<SchedulerAppointments>;
  retry(id: string): Promise<SchedulerAppointment>;
}

const S = `${APP}/scheduler`;
export const live = (http: Http): Api => ({
  setup: () => http.get(`${S}/connection`),
  save: (body) => http.put(`${S}/connection`, body),
  test: () => http.post<{ test: TestResult }>(`${S}/connection/test`, {}).then((r) => r.test),
  appointments: (q) => http.get(`${S}/appointments`, { query: { ...q } }),
  retry: (id) =>
    http.post<{ appointment: SchedulerAppointment }>(`${S}/appointments/${encodeURIComponent(id)}/retry`, {}).then((r) => r.appointment),
});

/** The fixture tenant is a workshop: every call answers as the API does while the switch is off. */
export const fixture = (): Api => {
  const off = () => Promise.reject(new ApiError(404, 'feature_disabled', null, { ok: false, error: 'feature_disabled' }));
  return new Proxy({} as Api, { get: () => off });
};
